import React, { useState, useEffect } from "react";
import {
  Breadcrumb,
  PageHeader,
  Spin,
  Table,
  message,
  Button,
  Popconfirm,
  Tag,
} from "antd";
import { Link } from "react-router-dom";
import Page from "_components/Page";
import TabMenu from "./TabMenu";
import WriteOffModal from "./WriteOffModal";
import { stockIssueService } from "_services";

function TiepIssueWriteOff({ match, history }) {
  const { id } = match.params;
  const { path } = match;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [writeOffValues, setWriteOffValues] = useState(new Map());
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [stockIssue, setStockIssue] = useState({});
  const [isWriteOffModalVisible, setIsWriteOffModalVisible] = useState(false);
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        await getStockIssue(id);
        await getWriteOffValues(id);
      } catch (error) {
        setError(error);
        message.error(error);
      }
    };

    fetchAllData();
  }, [id]);

  const getStockIssue = async (id) => {
    try {
      setLoading(true);
      const response = await stockIssueService.getByViewId(id);
      const stockIssue = response.content;
      setStockIssue(stockIssue);
      //onFill(stockIssue);
    } catch (error) {
      setError(error);
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getWriteOffValues = async (id) => {
    try {
      setLoading(true);
      const data = await stockIssueService.getWriteOffFromIssue(id);
      // Convert data.content into a Map
      const writeOffMap = new Map(
        data.content.map((item) => [item.id, { ...item, key: item.id }])
      );

      setWriteOffValues(writeOffMap);
    } catch (error) {
      setError(error);
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteButtonClick = async () => {
    try {
      setLoading(true);
      const params = { requestStatus: "COMPLETE" };
      await stockIssueService.updateStatus(id, params);
      message.success("Successfully completed the export!");
      history.push(`/stock-issue/${id}/summary`);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleWriteOffButtonClick = () => {
    if (selectedRowKeys.length === 0) {
      message.warning("Please select at least one stock item");
      return;
    }
    setIsWriteOffModalVisible(true);
  };

  const handleWriteOffSubmit = async (payload) => {
    try {
      setLoading(true);
      await stockIssueService.updateWriteOff(id, payload);
      message.success("Writeoff submitted successfully!");
      setIsWriteOffModalVisible(false);
      await getWriteOffValues(id);
    } catch (error) {
      setError(error);
      message.error("Failed to submit writeoff");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getActionButtons = () => {
    let buttons = [];

    if (
      Object.values(writeOffValues).some(
        (item) => item.stockItemStockStatus === "ISSUED"
      )
    ) {
      buttons.push(
        <Popconfirm
          title={`Are you sure to Complete this?`}
          onConfirm={(e) => {
            e.preventDefault();
            handleCompleteButtonClick();
          }}
          key="con-complete"
          placement="topLeft"
        >
          <Button key="btn-complete" htmlType="button" loading={loading}>
            Complete
          </Button>
        </Popconfirm>
      );
    } else if (stockIssue?.orderStage === "ISSUED") {
      buttons.push(
        <Button
          key="btn-writeoff"
          htmlType="button"
          loading={loading}
          onClick={handleWriteOffButtonClick}
        >
          Writeoff
        </Button>
      );
    }
    return buttons;
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: record?.stockItemStockStatus === "DELIVERED",
    }),
  };

  const getColumns = () => {
    let columns = [];

    columns.push({
      title: "Cus.Dec.No#",
      dataIndex: "receiptCustomRefNumber",
      key: "receiptCustomRefNumber",
    });

    columns.push({
      title: "HS code",
      dataIndex: "stockTypeCode",
      key: "stockTypeCode",
    });

    columns.push({
      title: "Description",
      dataIndex: "stockTypeName",
      key: "stockTypeName",
    });

    columns.push({
      title: "Import Type",
      dataIndex: "receiptType",
      key: "receiptType",
      render: (text, record) => {
        if (text === "LOCAL") return "INDIRECT";
        else if (text === "TIEP") return "DIRECT";
      },
    });

    columns.push({
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
    });

    columns.push({
      title: "Quantity",
      dataIndex: "issuedQuantity",
      key: "issuedQuantity",
    });

    columns.push({
      title: "Export Cus.Dec.No#",
      dataIndex: "customReferenceNumber",
      key: "customReferenceNumber",
    });

    columns.push({
      title: "Status",
      dataIndex: "stockItemStockStatus",
      key: "stockItemStockStatus",
      render: (text, record) => {
        let color = "";
        if (text === "DELIVERED") {
          color = "#87d068";
        }
        if (text === "ISSUED") {
          color = "#f50";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    });

    return columns;
  };

  return (
    <PageHeader
      title={
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to="/dashboard">Dashboard</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/stock-issue">Export List</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`${path}`.replace(":id", id)}>
              {stockIssue.issueNumber} ::{" "}
              <Tag
                color={`${
                  stockIssue.orderStage === "ISSUED"
                    ? "#2db7f5"
                    : stockIssue.orderStage === "IN_PROGRESS"
                    ? "#108ee9"
                    : "#87d068"
                }`}
              >
                {stockIssue.orderStage == "READY"
                  ? "UPDATE"
                  : stockIssue.orderStage}
              </Tag>
            </Link>
          </Breadcrumb.Item>
        </Breadcrumb>
      }
      extra={getActionButtons()}
    >
      <Page inner error={error}>
        <Spin tip="Loading..." spinning={loading}>
          <TabMenu menu="write-off" id={id} />

          <Table
            bordered
            pagination={false}
            rowSelection={rowSelection}
            columns={getColumns()}
            dataSource={Array.from(writeOffValues.values())}
            size="small"
          />
        </Spin>
      </Page>

      <WriteOffModal
        visible={isWriteOffModalVisible}
        onCancel={() => setIsWriteOffModalVisible(false)}
        onSubmit={handleWriteOffSubmit}
        selectedStockItems={selectedRowKeys.map((key) =>
          writeOffValues.get(key)
        )}
        loading={loading}
      />
    </PageHeader>
  );
}

export default TiepIssueWriteOff;
